<script>
    import { keyScope } from '../scripts/keyboard';
    import ActionButton from './components/ActionButton.svelte';
    import CopyButton from '../CopyButton.svelte';
    import PlaygroundRating from '../PlaygroundRating.svelte';
    import ApiClient from '../scripts/apiclient';
    import { createActionRunner } from '../scripts/ActionRunner';
    import PlaygroundHelper from '../scripts/playgroundhelper';

    const { busy, run } = createActionRunner();

    /**
     * The photo to approve.
     */
    export let photo;

    /**
     * The url of the photo file.
     */
    $: photoUrl = generatePhotoUrl(photo);

    /**
     * The playground of the photo.
     */
    $: playground = photo.playground;

    /**
     * Function to close the surrounding popup.
     */
    export let closePopup;

    /**
     * Generates a photo thumbnail url including a cache-busting timestamp at the end
     * 
     * @param photo
     */
    function generatePhotoUrl(photo) {
        return ApiClient.getThumbnailUrl(photo._id, 400) + '&q=' + new Date().getTime();
    }

    const rotateClockwise = run(() => rotate(true));

    const rotateCounterClockwise = run(() => rotate(false));

    function rotate(clockwise) {
        return ApiClient.rotatePhoto(photo, clockwise).then(() => {
            photoUrl = generatePhotoUrl(photo);
        });
    }

    const reject = run(() => ApiClient.rejectPhoto(photo).then(close));

    const approve = run(() => ApiClient.approvePhoto(photo).then(close));

    // This wrapper prevents the mouse event to be sent to close()
    function cancel() {
        close();
    }

    // Closing is possible while an action is running, so that a hanging action can be
    // abandoned and retried. The flag makes sure that an action finishing after that
    // doesn't close the popup a second time, closing whatever has been opened since.
    let closed = false;

    function close(updatedPhoto) {
        if (closed || !closePopup) {
            return;
        }

        if (updatedPhoto) {
            Object.assign(photo, updatedPhoto);
        }

        closed = true;
        closePopup();
    }

    keyScope({ Enter: approve, Escape: cancel, a: reject }, { modal: true });
</script>

<h3>{PlaygroundHelper.presentPlayground(playground, false)}</h3>
<span class="subtitle"><i class="fas fa-map-marker" /> <span class="id-text">{playground._id}</span><CopyButton text={playground._id} title="Kopiera lekplatsens id" /></span>
<span class="subtitle"><i class="fas fa-image" /> <span class="id-text">{photo._id}</span><CopyButton text={photo._id} title="Kopiera fotots id" /></span>
<PlaygroundRating {playground} />

<img class="photo" src={photoUrl} width="400" height="400" alt="Foto av lekplatsen" />

<div class="button-wrapper-wrapper">
    <div class="button-wrapper" style="float: right;">
        <button class="btn btn-sm" on:click={cancel}>Avbryt</button>
        <ActionButton {busy} action={reject} variant="btn-warning" title="Avslå fotot"><u>A</u>vslå</ActionButton>
        <ActionButton {busy} action={approve} name="approve" title="Godkänn fotot">Godkänn</ActionButton>
    </div>

    <!-- svelte-ignore a11y-missing-attribute -->
    <ActionButton {busy} action={rotateCounterClockwise} variant="btn-secondary" title="Rotera motsols"><img src="./assets/rotate-counter-clockwise.png" /></ActionButton>
    <!-- svelte-ignore a11y-missing-attribute -->
    <ActionButton {busy} action={rotateClockwise} variant="btn-secondary" title="Rotera medsols"><img src="./assets/rotate-clockwise.png" /></ActionButton>
    <div class="clearfix" />
</div>

<style>
    h3 {
        margin-bottom: 0;
    }

    .subtitle {
        color: #444;
        padding: 0;
        margin: 0;
    }

    /* The photo is asked for at 400px, which is wider than the dialog gets on a phone */
    .photo {
        max-width: 100%;
        height: auto;
    }

    .button-wrapper-wrapper {
        margin-top: 15px;
    }

    .button-wrapper {
        text-align: right;
    }

    /* Global, since the rotate buttons live in their own component */
    .button-wrapper-wrapper :global(.btn-secondary) {
        float: left;
        margin-right: 5px;
    }

    .button-wrapper-wrapper :global(.btn-secondary img) {
        width: 15px;
    }
</style>
